import { useCallback, useMemo, useState } from 'react';

import { useSJDatabase } from '@/shared/lib/sj-orm';
import { NotificationType } from '@/shared/lib/sj-orm/models/notification.dto';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';

export type NotificationContent = {
  title: string;
  message: string;
  date: Date;
};

export type NotificationListItem = NotificationContent & {
  id: string;
  isRead: boolean;
  type: NotificationType;
};

export const useNotifications = () => {
  const sjDb = useSJDatabase();
  const { keyPair } = useKeyPairStore();
  const [total, setTotal] = useState(0);

  const getNotificationsList = useCallback(
    async (isVisible = true, cursor?: { from: number; to: number }) => {
      const collectionItems =
        sjDb.collections.notification?.findMany((item) => item.isVisible === isVisible) ?? [];
      const items = [...collectionItems].reverse();

      setTotal(items.length);

      if (!items.length) return [];
      const result: NotificationListItem[] = [];

      const partItems = cursor ? items.slice(cursor.from, cursor.to + 1) : items;

      for (const item of partItems) {
        result.push({
          ...item,
          title: item.title ?? '',
          message: item.message ?? '',
          isRead: item.isRead[keyPair?.publicKey || ''],
          type: item.type,
        });
      }

      return result;
    },
    [sjDb, keyPair],
  );

  const updateRead = useCallback(
    (id: string, state: boolean = true, publicKey?: string) => {
      try {
        const notification = sjDb.collections.notification?.findById(id);
        if (!notification) return false;

        const key = publicKey ?? keyPair?.publicKey;
        if (!key) return false;

        if (notification.isRead[key] !== state) {
          notification.isRead[key] = state;
          sjDb.collections.notification?.update(notification);
        }

        return true;
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    [sjDb, keyPair],
  );

  const markAllRead = useCallback(() => {
    const items = sjDb.collections.notification?.findMany(() => true) ?? [];
    items.forEach((item) => updateRead(item.id));
  }, [sjDb, updateRead]);

  return useMemo(
    () => ({
      getNotificationsList,
      updateRead,
      markAllRead,
      total,
    }),
    [getNotificationsList, updateRead, markAllRead, total],
  );
};
